import { useQuery } from '@tanstack/react-query';
import { describeParityErrors, isParityValid, parsePlaybook, validateParity } from '../config/parser';
import { PlaybookBundle, PlaybookLang } from '../config/types';

const DOCS_PATH = '/user-guide-docs';
const getFileUrl = (lang: PlaybookLang) => `${DOCS_PATH}/playbook-host-portal-${lang}.md`;

export class PlaybookParityError extends Error {
  details: string[];
  constructor(details: string[]) {
    super('Playbook VI and EN files do not match');
    this.name = 'PlaybookParityError';
    this.details = details;
  }
}

const fetchMarkdown = async (lang: PlaybookLang): Promise<string> => {
  const response = await fetch(getFileUrl(lang));
  if (!response.ok) throw new Error(`Cannot load ${getFileUrl(lang)} (${response.status})`);
  return response.text();
};

/** Loads both language files once and rejects the pair when their section codes differ. */
const loadPlaybook = async (): Promise<PlaybookBundle> => {
  const [viText, enText] = await Promise.all([fetchMarkdown('vi'), fetchMarkdown('en')]);
  const vi = parsePlaybook(viText, 'vi');
  const en = parsePlaybook(enText, 'en');

  const parity = validateParity(vi, en);
  if (!isParityValid(parity)) {
    const details = describeParityErrors(parity);
    console.error('[playbook] VI/EN files do not match', details);
    throw new PlaybookParityError(details);
  }
  return { vi, en };
};

/** Fetches the guide files. Pass `enabled=false` until the user opens the guide for the first time. */
export const usePlaybookData = (enabled: boolean) =>
  useQuery<PlaybookBundle, Error>(['playbook-host-portal'], loadPlaybook, {
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
